import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query'
import { wasteSubcategoriesApi } from '@/lib/api'
import type { WasteSubcategory } from '@/types'

export const wasteSubcategoryKeys = {
  all: ['waste-subcategories'] as const,
  list: () => [...wasteSubcategoryKeys.all, 'list'] as const,
  detail: (id: number) => [...wasteSubcategoryKeys.all, 'detail', id] as const,
}

export function useWasteSubcategories(): UseQueryResult<WasteSubcategory[]> {
  return useQuery({
    queryKey: wasteSubcategoryKeys.list(),
    queryFn: async () => {
      const res = await wasteSubcategoriesApi.list()
      return res.data
    },
    staleTime: 1000 * 60 * 10,
  })
}

export function useCreateWasteSubcategory() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: { waste_category_id: number; name: string; is_active?: boolean }) =>
      wasteSubcategoriesApi.create(data).then((r) => r.data),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: wasteSubcategoryKeys.list() }),
  })
}

export function useUpdateWasteSubcategory() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: number
      data: { waste_category_id: number; name: string; is_active?: boolean }
    }) => wasteSubcategoriesApi.update(id, data).then((r) => r.data),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: wasteSubcategoryKeys.list() }),
  })
}

export function useDeleteWasteSubcategory() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => wasteSubcategoriesApi.delete(id),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: wasteSubcategoryKeys.list() }),
  })
}
